import Footer from "@/components/Footer";
import { Header } from "@/components/landing/Header";
import { Suspense } from "react";

const PrivacyPolicy = () => {
    return (
        <>
            <Header />
            <main className="flex min-h-full items-center justify-center bg-stone-50 dark:bg-stone-950 px-4 py-24">
                <div className="container max-w-4xl mx-auto px-4">
                    <h1 className="text-3xl font-bold mb-8 dark:text-white">Privacy Policy</h1>

                    <div className="text-sm text-gray-600 dark:text-stone-500 mb-8">Last Updated: February 16, 2025</div>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4 dark:text-white">1. Introduction</h2>
                        <p className="mb-4 dark:text-stone-500">At IELTS With AI ("we," "our," or "us"), we take your privacy seriously. This Privacy Policy explains how we collect, use, and protect your personal information when you use our IELTS writing practice service (the "Service").</p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4">2. Information We Collect</h2>
                        <div className="mb-4">
                            <h3 className="text-xl font-medium mb-2">2.1. Information You Provide</h3>
                            <ul className="list-disc pl-8 mb-4 dark:text-stone-500">
                                <li className="mb-2">Email address (for account creation and communication)</li>
                                <li className="mb-2">Payment information (processed by Stripe)</li>
                                <li className="mb-2">Essay content and writing submissions</li>
                            </ul>

                            <h3 className="text-xl font-medium mb-2">2.2. Automatically Collected Information</h3>
                            <ul className="list-disc pl-8 mb-4 dark:text-stone-500">
                                <li className="mb-2">Usage data (number of submissions, frequency of use)</li>
                                <li className="mb-2">Device information (browser type, IP address)</li>
                                <li className="mb-2">Cookies and similar tracking technologies</li>
                            </ul>
                        </div>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4">3. How We Use Your Information</h2>
                        <div className="mb-4 dark:text-stone-500">
                            <p className="mb-2">We use your information for the following purposes:</p>
                            <ul className="list-disc pl-8 mb-4">
                                <li className="mb-2">To provide and maintain our Service</li>
                                <li className="mb-2">To process your payments</li>
                                <li className="mb-2">To evaluate your essays and provide feedback</li>
                                <li className="mb-2">To monitor Service usage and prevent abuse</li>
                                <li className="mb-2">To communicate with you about your account</li>
                                <li className="mb-2">To improve our Service</li>
                            </ul>
                        </div>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4">4. Data Processing and Third-Party Services</h2>
                        <div className="mb-4 dark:text-stone-500">
                            <p className="mb-2">We use the following third-party services to process your data:</p>
                            <ul className="list-disc pl-8 mb-4">
                                <li className="mb-2">Stripe (payment processing)</li>
                                <li className="mb-2">OpenAI (essay evaluation)</li>
                            </ul>
                            <p className="mb-4 dark:text-stone-500">Important notes about data processing:</p>
                            <ul className="list-disc pl-8 mb-4">
                                <li className="mb-2">Your essays are processed using OpenAI's services but are not used for AI training</li>
                                <li className="mb-2">Payment information is processed securely by Stripe and we do not store your full payment details</li>
                                <li className="mb-2">We store your essays indefinitely to allow you to access your practice history</li>
                            </ul>
                        </div>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4">5. Data Storage and Security</h2>
                        <div className="mb-4 dark:text-stone-500">
                            <p className="mb-4">We implement appropriate technical and organizational measures to protect your personal information, including:</p>
                            <ul className="list-disc pl-8 mb-4">
                                <li className="mb-2">Secure data transmission using SSL/TLS encryption</li>
                                <li className="mb-2">Regular security assessments and updates</li>
                                <li className="mb-2">Limited access to personal information by authorized personnel only</li>
                            </ul>
                        </div>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4">6. Your Rights</h2>
                        <div className="mb-4 dark:text-stone-500">
                            <p className="mb-4">Under applicable data protection laws, you have the following rights:</p>
                            <ul className="list-disc pl-8 mb-4">
                                <li className="mb-2">Right to access your personal data</li>
                                <li className="mb-2">Right to correct inaccurate personal data</li>
                                <li className="mb-2">Right to request deletion of your personal data</li>
                                <li className="mb-2">Right to restrict processing of your personal data</li>
                                <li className="mb-2">Right to data portability</li>
                                <li className="mb-2">Right to object to processing of your personal data</li>
                            </ul>
                            <p className="mb-4">To exercise any of these rights, please contact us at contact@ieltswith.ai.</p>
                        </div>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4">7. Cookies</h2>
                        <div className="mb-4 dark:text-stone-500">
                            <p className="mb-4">We use cookies and similar tracking technologies to improve your experience with our Service. You can control cookie settings through your browser preferences.</p>
                            <p className="mb-4">Essential cookies are required for the Service to function properly. Other cookies are used for:</p>
                            <ul className="list-disc pl-8 mb-4">
                                <li className="mb-2">Authentication and session management</li>
                                <li className="mb-2">Remembering your preferences</li>
                                <li className="mb-2">Analytics and performance monitoring</li>
                            </ul>
                        </div>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4">8. International Data Transfers</h2>
                        <p className="mb-4 dark:text-stone-500">Your information may be transferred to and processed in countries other than your country of residence. These countries may have different data protection laws. When we transfer your data, we ensure appropriate safeguards are in place to protect your information.</p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4">9. Children's Privacy</h2>
                        <p className="mb-4 dark:text-stone-500">Our Service is not directed to children under the age of 16. If you become aware that a child has provided us with personal information without parental consent, please contact us.</p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4">10. Changes to This Privacy Policy</h2>
                        <p className="mb-4 dark:text-stone-500">We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.</p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4">11. Contact Us</h2>
                        <p className="mb-4 dark:text-stone-500">If you have any questions about this Privacy Policy or our data practices, please contact us at contact@ieltswith.ai.</p>
                    </section>
                </div>
            </main>
            <Suspense fallback={<div className="h-16" />}>
                <Footer />
            </Suspense>
        </>
    );
};

export default PrivacyPolicy;
